"use client";

import { useState } from "react";
import type { FormState } from "@/modules/shared/forms";
import { formatLebanesePhoneFull } from "@/modules/identity/surfaces";
import { CONSUMER_DELETION, type DeletionCopy } from "./deletionCopy";
import { Banner, FIELD_LABEL, Field } from "./ui";

/**
 * ConsumerProfileScreen (docs/ux/screens/ConsumerProfileScreen.md), shared by
 * both frontends (R22 T13).
 *
 * Each form takes its command as a prop: an
 * API call in the static web app. Everything that matters is the same on both,
 * and on this screen that includes the part it would be worst to have twice,
 * the H34 disclosure. What a deletion actually does to somebody's history is a
 * ruling with three conditions attached, and the list stating it has to be the
 * same list on every surface that offers the button.
 *
 * The Consumer's phone is the verified identity (H22) and is not editable here.
 */

/** Submit, with the pending label each form specifies. */
function SaveButton({
  children,
  pendingLabel,
  pending,
  full = true,
}: {
  children: React.ReactNode;
  pendingLabel: string;
  pending: boolean;
  full?: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={pending}
      className={`inline-flex min-h-11 items-center justify-center rounded-lg bg-primary px-4 py-2.5 font-semibold text-on-primary transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60 ${
        full ? "w-full" : ""
      }`}
    >
      {pending ? pendingLabel : children}
    </button>
  );
}

/** One command, its pending flag and its answer. */
function useSubmit() {
  const [state, setState] = useState<FormState>({});
  const [pending, setPending] = useState(false);
  const run = (command: () => Promise<FormState>) => {
    setPending(true);
    setState({});
    void command()
      .then(setState)
      .finally(() => setPending(false));
  };
  return { state, pending, run };
}

// ── Display name (phone is read-only, see the command's note on H22) ─────────

export function ProfileNameForm({
  firstName,
  lastName,
  phone,
  phoneVerified,
  save,
}: {
  firstName: string;
  lastName: string;
  phone: string | null;
  phoneVerified: boolean;
  save: (firstName: string, lastName: string) => Promise<FormState>;
}) {
  const { state, pending, run } = useSubmit();
  const [first, setFirst] = useState(firstName);
  const [last, setLast] = useState(lastName);
  return (
    <>
      <h2 className="mb-4 text-lg font-semibold text-ink">Account</h2>
      {state.success && <Banner tone="success">{state.success}</Banner>}
      {state.formError && <Banner>{state.formError}</Banner>}

      <div className="mb-4">
        <span className={FIELD_LABEL}>Phone number</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-body">
            {phone ? formatLebanesePhoneFull(phone) : "Not set"}
          </span>
          {phoneVerified && (
            <span className="rounded-lg bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
              Verified
            </span>
          )}
        </div>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          run(() => save(first, last));
        }}
      >
        <fieldset disabled={pending} className="disabled:opacity-70">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="First name"
              name="firstName"
              value={first}
              onChange={setFirst}
              error={state.fieldErrors?.firstName}
              autoComplete="given-name"
            />
            <Field
              label="Last name"
              name="lastName"
              value={last}
              onChange={setLast}
              error={state.fieldErrors?.lastName}
              autoComplete="family-name"
            />
          </div>
          <SaveButton pendingLabel="Saving…" pending={pending}>
            Save name
          </SaveButton>
        </fieldset>
      </form>
    </>
  );
}

// ── Change password: current + new + confirm ──────────────────────────────────

export function ChangePasswordForm({
  save,
}: {
  save: (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ) => Promise<FormState>;
}) {
  const { state, pending, run } = useSubmit();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  return (
    <>
      <h2 className="mb-4 text-lg font-semibold text-ink">Change password</h2>
      {state.success && <Banner tone="success">{state.success}</Banner>}
      {state.formError && <Banner>{state.formError}</Banner>}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          run(() =>
            save(current, next, confirm).then((answer) => {
              // Clear the boxes only when it worked. A refusal keeps what was
              // typed, because "current password is incorrect" is often a typo
              // in one field and retyping all three is a punishment.
              if (answer.success) {
                setCurrent("");
                setNext("");
                setConfirm("");
              }
              return answer;
            }),
          );
        }}
      >
        <fieldset disabled={pending} className="disabled:opacity-70">
          <Field
            label="Current password"
            name="currentPassword"
            type="password"
            value={current}
            onChange={setCurrent}
            error={state.fieldErrors?.currentPassword}
            autoComplete="current-password"
          />
          <Field
            label="New password"
            name="newPassword"
            type="password"
            value={next}
            onChange={setNext}
            error={state.fieldErrors?.newPassword}
            autoComplete="new-password"
          />
          <Field
            label="Confirm new password"
            name="confirmPassword"
            type="password"
            value={confirm}
            onChange={setConfirm}
            error={state.fieldErrors?.confirmPassword}
            autoComplete="new-password"
          />
          <SaveButton pendingLabel="Saving…" pending={pending}>
            Change password
          </SaveButton>
        </fieldset>
      </form>
    </>
  );
}

// ── Delete account (H34, ruled Option A: anonymise and keep) ─────────────────

/**
 * The web half of account deletion. Google Play requires a deletion route
 * reachable from a browser rather than only from inside an installed app, and
 * somebody who has already uninstalled the app is exactly the person most
 * likely to want one.
 *
 * **The list of what a clinic keeps is above the password field, not below it.**
 * H34's condition 3 is that the person is told what actually happens *before*
 * they confirm; a disclosure underneath the button is a disclosure nobody read.
 *
 * **The form is behind one click.** Not a modal somebody dismisses by reflex,
 * and not open by default either: a destructive form permanently expanded on a
 * settings page is a destructive form somebody eventually tabs into.
 */
export function DeleteAccountForm({
  destroy,
  copy = CONSUMER_DELETION,
}: {
  /**
   * What this deletion does, said before the password (H34 condition 3). The
   * Consumer's by default; the member's on the business settings (H58).
   */
  copy?: DeletionCopy;
  /**
   * Delete the account. On success the host **navigates away** rather than
   * returning a state: the page it was called from reads a profile that no
   * longer exists, so re-rendering it would show a data-unavailable panel
   * where an account used to be, which reads as a fault rather than as the
   * thing the person just asked for.
   */
  destroy: (currentPassword: string) => Promise<FormState>;
}) {
  const { state, pending, run } = useSubmit();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");

  if (!open) {
    return (
      <div>
        <h2 className="mb-1 text-lg font-semibold text-ink">Delete account</h2>
        <p className="mb-4 text-sm text-muted">{copy.summary}</p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-lg border border-danger px-4 py-2 text-sm font-semibold text-danger transition-colors hover:bg-danger hover:text-white"
        >
          Delete account
        </button>
      </div>
    );
  }

  return (
    <>
      <h2 className="mb-4 text-lg font-semibold text-ink">Delete account</h2>
      {state.formError && <Banner>{state.formError}</Banner>}

      <div className="mb-4 space-y-3 text-sm leading-relaxed">
        {copy.sections.map((section) => (
          <div key={section.title}>
            <p className={FIELD_LABEL}>{section.title}</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-body">
              {section.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
        <p className="font-semibold text-ink">{copy.finalLine}</p>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          run(() => destroy(password));
        }}
      >
        <fieldset disabled={pending} className="disabled:opacity-70">
          <Field
            label="Your password"
            name="currentPassword"
            type="password"
            value={password}
            onChange={setPassword}
            error={state.fieldErrors?.currentPassword}
            autoComplete="current-password"
            help="Typing it is how we know it is you."
          />
          <div className="flex items-center gap-3">
            <SaveButton pendingLabel="Deleting…" pending={pending} full={false}>
              Delete my account
            </SaveButton>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-sm font-semibold text-muted transition-colors hover:text-body"
            >
              Cancel
            </button>
          </div>
        </fieldset>
      </form>
    </>
  );
}
